import { useIsAdmin } from '../auth/hooks/useIsAdmin';

import {
  Grid,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListSubheader,
  ListItemIcon,
} from '@mui/material';
import GreenButton from '../../components/GreenButton';
import { Location } from '../../types';
import { useState } from 'react';
import LocationDialog from './LocationDialog';
import DisableWeatherConfirmationDialog from './DisableWeatherConfirmationDialog';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import ExportWeatherDialog from './ExportWeatherDialog';
import WeatherStationsIcon from './WeatherStationsIcon';
import { useLocations } from './api/getLocations';
import { useDeleteLocation } from './api/deleteLocation';
import { useUpdateLocation } from './api/updateLocation';
import InfoPopup from 'components/InfoPopup';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';

const ManageLocationsPage = () => {
  const isAdmin = useIsAdmin();
  const { data: systemModules } = useSystemModules({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [disableWeatherDialogOpen, setDisableWeatherDialogOpen] =
    useState(false);
  const { data: locations } = useLocations({});
  const deleteLocation = useDeleteLocation({});
  const updateLocation = useUpdateLocation({});
  const [selectedLocationId, setSelectedLocationId] = useState<null | number>(
    null
  );
  const [snackbar, setSnackBar] = useState<{
    severity: 'success' | 'error';
    open: boolean;
    message: string;
  }>({
    severity: 'success',
    open: false,
    message: '',
  });

  const handleCloseSnackbar = () => {
    setSnackBar({ ...snackbar, open: false });
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedLocationId(null);
  };
  const handleRowClicked = (locationId: number) => {
    setSelectedLocationId(locationId);
  };
  const onSubmitDelete = async () => {
    if (!selectedLocationId) return;
    deleteLocation.mutateAsync(selectedLocationId);
    setSelectedLocationId(null);
    setDeleteDialogOpen(false);
  };
  const onCloseDelete = () => {
    setDeleteDialogOpen(false);
  };
  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };
  const handleNew = () => {
    setSelectedLocationId(null);
    setDialogOpen(true);
  };

  const handleDisableWeatherSubmit = async () => {
    if (selectedLocationId === null) return;

    try {
      await updateLocation.mutateAsync({
        data: { enabled: false },
        locationId: selectedLocationId,
      });
    } catch (err: any) {
      setSnackBar({
        severity: 'error',
        open: true,
        message: 'Failed to disable weather',
      });
    }

    setDisableWeatherDialogOpen(false);
    setSelectedLocationId(null);
  };

  const handleToggle = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    changedLocation: Location
  ) => {
    event.stopPropagation();
    setSelectedLocationId(changedLocation.id);
    if (changedLocation.enabled) {
      setDisableWeatherDialogOpen(true);
      return;
    }

    try {
      await updateLocation.mutateAsync({
        data: { enabled: !changedLocation.enabled },
        locationId: changedLocation.id,
      });
    } catch (err: any) {
      setSnackBar({
        severity: 'error',
        open: true,
        message: 'Location limit exceeded',
      });
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item xs={12} alignItems='center' spacing={1}>
          <Grid item>
            <GreenButton name='New Location' onClick={handleNew}></GreenButton>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              onClick={() => setDialogOpen(true)}
              disabled={!selectedLocationId}
            >
              Edit Location
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='error'
              disabled={!selectedLocationId}
              onClick={handleDelete}
            >
              Delete Location
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              disabled={!selectedLocationId}
              onClick={() => setExportDialogOpen(true)}
            >
              Export Data
            </Button>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item component={Paper} xs>
            <List>
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <ListSubheader>Location</ListSubheader>
                </Grid>
                {systemModules?.WEATHER && (
                  <Grid item>
                    <ListSubheader>Weather Enabled</ListSubheader>
                  </Grid>
                )}
              </Grid>
              {locations &&
                locations.map((location) => (
                  <ListItem
                    button
                    key={location.id}
                    selected={selectedLocationId === location.id}
                    onClick={() => handleRowClicked(location.id)}
                    secondaryAction={
                      systemModules?.WEATHER ? (
                        <Checkbox
                          edge='end'
                          tabIndex={-1}
                          checked={!!location.enabled}
                          onClick={(event) => handleToggle(event, location)}
                          disabled={!location.coord_1 || !location.coord_2}
                        />
                      ) : undefined
                    }
                  >
                    <ListItemText>{location.name}</ListItemText>
                    {location.weather_station_id && (
                      <ListItemIcon>
                        <WeatherStationsIcon
                          stationId={location.weather_station_id}
                        />
                      </ListItemIcon>
                    )}
                  </ListItem>
                ))}
            </List>
          </Grid>
        </Grid>
        <LocationDialog
          open={dialogOpen}
          handleClose={handleClose}
          location={
            locations &&
            locations.find((location) => location.id === selectedLocationId)
          }
          setSnackBar={setSnackBar}
        />
        <DisableWeatherConfirmationDialog
          open={disableWeatherDialogOpen}
          locationName={
            (locations &&
              locations.find((location) => location.id === selectedLocationId)
                ?.name) ||
            ''
          }
          onSubmit={handleDisableWeatherSubmit}
          onClose={() => setDisableWeatherDialogOpen(false)}
        />
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          itemToDelete={
            (locations &&
              locations.find((location) => location.id === selectedLocationId)
                ?.name) ||
            ''
          }
          onSubmit={onSubmitDelete}
          onClose={onCloseDelete}
        />
        <ExportWeatherDialog
          open={exportDialogOpen}
          location={
            locations &&
            locations.find((location) => location.id === selectedLocationId)
          }
          handleClose={() => setExportDialogOpen(false)}
        />
      </Grid>

      <InfoPopup
        openCondition={snackbar.open}
        error={snackbar.severity === 'error'}
        success={snackbar.severity === 'success'}
        title={null}
        message={snackbar.message}
        handleClose={handleCloseSnackbar}
      />
    </>
  );
};

export default ManageLocationsPage;
